/**
 * Authentication store using Zustand.
 *
 * Simplified 2-tier system: admin / approved.
 */
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { apiClient } from '@/lib/api';
import { isNative, unregisterPush } from '@/lib/capacitor';

interface User {
  id: string | number;
  email: string;
  name?: string;
  tier: 'approved' | 'admin';
  isAdmin: boolean;
}

export type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated' | 'error';

interface AuthState {
  // State
  user: User | null;
  status: AuthStatus;
  error: string | null;

  // Computed
  isPro: boolean;
  isAdmin: boolean;

  // Actions
  checkAuth: () => Promise<void>;
  login: () => void;
  logout: () => Promise<void>;
  clearError: () => void;
}

function authFlags(user: User | null) {
  return {
    isPro: user?.tier === 'approved' || user?.tier === 'admin',
    isAdmin: user?.tier === 'admin',
  };
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state - always start with 'checking' to force auth check
        user: null,
        status: 'checking' as AuthStatus,
        error: null,

        isPro: false,
        isAdmin: false,

        // Check current auth status
        checkAuth: async () => {
          set({ status: 'checking', error: null });
          try {
            const response = await apiClient.auth.status();
            const data = response.data;

            if (data.authenticated && data.user) {
              const user = {
                id: data.user.id,
                email: data.user.email,
                name: data.user.name,
                tier: data.user.tier,
                isAdmin: data.user.tier === 'admin',
              };
              set({
                user,
                status: 'authenticated',
                ...authFlags(user),
              });
            } else {
              set({
                user: null,
                status: 'unauthenticated',
                ...authFlags(null),
              });
            }
          } catch {
            set({
              user: null,
              status: 'error',
              error: 'Failed to check authentication',
              ...authFlags(null),
            });
          }
        },

        // Redirect to OAuth login
        // BP-088: On Capacitor (iOS), use Browser.open() (SFSafariViewController) to
        // keep WKWebView on our page. Generate a session ID so the app can claim the
        // JWT when the user returns (foreground fallback for custom URL scheme).
        login: () => {
          if (isNative) {
            const sid = crypto.randomUUID();
            localStorage.setItem('__pa_oauth_sid', sid);
            localStorage.setItem('__pa_oauth_ts', Date.now().toString());
            import('@capacitor/browser').then(({ Browser }) => {
              Browser.open({ url: `https://griosai.com/auth/google?source=capacitor&sid=${sid}` });
            });
          } else {
            window.location.href = '/auth/google';
          }
        },

        // Logout
        logout: async () => {
          // BP-060 FIX: Unsubscribe device push token before clearing auth
          try {
            await unregisterPush();
          } catch {
            // Best effort — token will be overwritten on next login
          }
          try {
            await apiClient.auth.logout();
          } catch {
            // Ignore errors, clear state anyway
          }
          set({
            user: null,
            status: 'unauthenticated',
            error: null,
            ...authFlags(null),
          });
          window.location.href = '/login';
        },

        // Clear error
        clearError: () => set({ error: null }),
      }),
      {
        name: 'auth-store',
        // Only persist user data, NOT auth status - always verify with server
        partialize: (state) => ({
          user: state.user,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);

useAuthStore.subscribe((state, previousState) => {
  if (state.user === previousState.user) return;

  const nextFlags = authFlags(state.user);
  if (state.isPro !== nextFlags.isPro || state.isAdmin !== nextFlags.isAdmin) {
    useAuthStore.setState(nextFlags);
  }
});
